import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import MatchBuckets from "@/components/MatchBuckets";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, RefreshCw, Building2, MapPin, Users2, AlertTriangle, Loader2 } from "lucide-react";

const PRIORITY_COLOR = { Urgent: "bg-red-50 text-red-700 border-red-200", High: "bg-amber-50 text-amber-700 border-amber-200", Normal: "bg-secondary text-foreground", Future: "bg-slate-100 text-slate-600" };
const listify = (a) => (Array.isArray(a) ? a : []);

export default function PositionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pos, setPos] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [rematching, setRematching] = useState(false);
  const pollRef = useRef(null);

  const loadMatches = useCallback(() => api.get(`/positions/${id}/matches`).then((r) => setMatchData(r.data)).catch(() => {}), [id]);

  useEffect(() => {
    api.get(`/positions/${id}`).then((r) => setPos(r.data)).catch(() => {});
    loadMatches();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [id, loadMatches]);

  const rematch = async () => {
    setRematching(true);
    await api.post(`/positions/${id}/rematch`);
    toast.success("Re-matching against all candidates…");
    let n = 0;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      n += 1;
      await loadMatches();
      if (n > 12) { clearInterval(pollRef.current); pollRef.current = null; setRematching(false); }
    }, 2500);
  };

  if (!pos) return <div className="p-8">Loading…</div>;

  const Chips = ({ label, items }) => listify(items).length > 0 && (
    <div><div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5">{listify(items).map((s) => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>)}</div></div>
  );

  return (
    <div className="p-8 max-w-5xl">
      <button onClick={() => navigate("/positions")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"><ArrowLeft size={15} /> Back to positions</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6 fade-up">
        <div>
          <h1 className="font-head text-3xl font-semibold tracking-tight">{pos.primary_title}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-3 text-sm text-muted-foreground">
            {pos.professional_category && <Badge variant="secondary">{pos.professional_category}</Badge>}
            {pos.priority && <Badge variant="outline" className={PRIORITY_COLOR[pos.priority] || ""}>{pos.priority} priority</Badge>}
            {pos.client && <span className="flex items-center gap-1"><Building2 size={14} />{pos.client}</span>}
            {pos.school_location && <span className="flex items-center gap-1"><MapPin size={14} />{pos.school_location}</span>}
            {pos.number_of_openings > 0 && <span className="flex items-center gap-1"><Users2 size={14} />{pos.number_of_openings} opening{pos.number_of_openings !== 1 && "s"}</span>}
          </div>
        </div>
        <Button onClick={rematch} disabled={rematching} data-testid="position-rematch-btn" variant="outline" className="shadow-card">
          {rematching ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <RefreshCw size={15} className="mr-1.5" />}Re-run matches
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-5">
          <div className="bg-card border border-border rounded-lg shadow-card p-5 space-y-4">
            <h3 className="font-head font-semibold">Requirements</h3>
            {pos.description && <p className="text-sm text-foreground/75">{pos.description}</p>}
            {pos.required_education && <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Required education</div><div className="text-sm">{pos.required_education}</div></div>}
            {pos.required_years_experience > 0 && <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Experience</div><div className="text-sm">{pos.required_years_experience}+ years</div></div>}
            <Chips label="Required licenses" items={pos.required_licenses} />
            <Chips label="Required certifications" items={pos.required_certifications} />
            <Chips label="Required skills" items={pos.required_skills} />
            <Chips label="Preferred skills" items={pos.preferred_skills} />
            <Chips label="Populations served" items={pos.populations_served} />
            <Chips label="Mandatory qualifications" items={pos.mandatory_requirements} />
          </div>
          {listify(pos.needs_review).length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-2"><AlertTriangle size={15} /> Needs recruiter review</div>
              <div className="flex flex-wrap gap-1.5">{listify(pos.needs_review).map((s) => <Badge key={s} variant="outline" className="text-xs bg-white border-amber-200 text-amber-700">{s}</Badge>)}</div>
            </div>
          )}
          {(pos.bill_rate || pos.pay_rate || pos.internal_notes) && (
            <div className="bg-card border border-border rounded-lg shadow-card p-5 space-y-2 text-sm">
              <h3 className="font-head font-semibold mb-1">TAG internal</h3>
              {pos.recruiter_assigned && <div><span className="text-muted-foreground">Recruiter:</span> {pos.recruiter_assigned}</div>}
              {pos.bill_rate && <div><span className="text-muted-foreground">Bill rate:</span> {pos.bill_rate}</div>}
              {pos.pay_rate && <div><span className="text-muted-foreground">Pay rate:</span> {pos.pay_rate}</div>}
              {pos.internal_notes && <div className="text-foreground/75 italic">{pos.internal_notes}</div>}
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-semibold text-lg">Candidate matches</h3>
            {matchData && <span className="text-sm text-muted-foreground tabular-nums">{matchData.matched} of {matchData.total_candidates} candidates scored</span>}
          </div>
          {matchData && matchData.matched === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-lg bg-card">
              <p className="text-muted-foreground text-sm">No matches yet. Click "Re-run matches" to score candidates against this position.</p>
            </div>
          ) : <MatchBuckets matchData={matchData} />}
        </div>
      </div>
    </div>
  );
}
